import { useAuth } from '../contexts/AuthContext';
import type { UserRole } from '../types/User';
import { isFinanciallyBlind, isKnownRole, roleLevel } from '../utils/roles';

interface UseRoleResult {
  role: UserRole | null;
  isOwner: boolean;
  /** Admin or owner. */
  isAdmin: boolean;
  /** Planner level or above (planner, contractor, admin, owner). */
  isPlanner: boolean;
  isContractor: boolean;
  /** Any signed-in person with a known role. */
  isViewer: boolean;
  /** Can create, update, or delete operational data (planner level or above) */
  canEdit: boolean;
  /** Can delete profiles, change portal settings (owner only) */
  canDelete: boolean;
  /** May see monetary data. Everyone except a contractor, an unknown role, or
   *  a person flagged financially blind. The flag hides money from any role. */
  canViewFinancials: boolean;
}

export function useRole(): UseRoleResult {
  const { user } = useAuth();
  const role = isKnownRole(user?.role) ? (user!.role as UserRole) : null;
  const level = roleLevel(role);

  return {
    role,
    canViewFinancials: role !== null && !isFinanciallyBlind(role, user?.financially_blind),
    isOwner: role === 'owner',
    isAdmin: role === 'owner' || role === 'admin',
    isPlanner: level >= 2,
    isContractor: role === 'contractor',
    isViewer: level >= 1,
    canEdit: level >= 2,
    canDelete: role === 'owner',
  };
}
